import React, { useMemo, useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, Dimensions, Modal, TextInput, Pressable, ScrollView, Alert, Platform } from 'react-native';
import PrimaryButton from '../components/PrimaryButton';
import colors from '../theme/colors';
import { useTableContext } from '../context/TableContext';
import { generatePixQRCode, checkPaymentStatus } from '../config/mercadopago';
import PixQRCode from '../components/PixQRCode';

const { width, height } = Dimensions.get('window');
const isSmallScreen = width < 480;
const reserveModalMaxHeight = Math.round(height * 0.9);

export default function HomeScreen({ onNavigate }) {
  const [isConsumptionModalVisible, setConsumptionModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formError, setFormError] = useState('');
  const [consumptionResults, setConsumptionResults] = useState([]);
  const [consumptionMessage, setConsumptionMessage] = useState('');
  const [isReserveModalVisible, setReserveModalVisible] = useState(false);
  const [reserveName, setReserveName] = useState('');
  const [reserveCpf, setReserveCpf] = useState('');
  const [reserveAddress, setReserveAddress] = useState('');
  const [reservePhone, setReservePhone] = useState('');
  const [reserveError, setReserveError] = useState('');
  const [reserveFeedback, setReserveFeedback] = useState('');
  const [isSelectTableModalVisible, setSelectTableModalVisible] = useState(false);
  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const [isReservationSuccessModalVisible, setIsReservationSuccessModalVisible] = useState(false);
  const [reservationData, setReservationData] = useState(null);
  const [isKioskModalVisible, setKioskModalVisible] = useState(false);
  const [kioskPassword, setKioskPassword] = useState('');
  const [kioskPasswordError, setKioskPasswordError] = useState('');
  const [isKioskMode, setIsKioskMode] = useState(false);
  const [isBuyChipsModalVisible, setIsBuyChipsModalVisible] = useState(false);
  const [selectedChipsPackage, setSelectedChipsPackage] = useState(null);
  const [isPixPaymentModalVisible, setIsPixPaymentModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [chipTicketData, setChipTicketData] = useState(null);
  const [pixQrCode, setPixQrCode] = useState(null);
  const [pixQrCodeBase64, setPixQrCodeBase64] = useState(null);
  const [pixQrCodeUrl, setPixQrCodeUrl] = useState(null);
  const [pixExpiration, setPixExpiration] = useState(null);
  const [pixTimeLeft, setPixTimeLeft] = useState(null);
  const [pixExpired, setPixExpired] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isLoadingPix, setIsLoadingPix] = useState(false);
  const [isBuyerInfoModalVisible, setIsBuyerInfoModalVisible] = useState(false);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerDocument, setBuyerDocument] = useState('');
  const [buyerDocumentType, setBuyerDocumentType] = useState('cpf');
  const [buyerInfoError, setBuyerInfoError] = useState('');
  const [buyerInfo, setBuyerInfo] = useState(null);
  const countdownIntervalRef = useRef(null);
  const statusIntervalRef = useRef(null);

  const [kioskFunctions, setKioskFunctions] = useState(() => {
    if (typeof window === 'undefined') {
      return {
        openTable: true,
        openTables: true,
        buyChips: true,
        reserve: true,
        consumption: true
      };
    }
    try {
      const stored = window.localStorage.getItem('kioskFunctions');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Erro ao carregar configurações do totem:', error);
    }
    return {
      openTable: true,
      openTables: true,
      buyChips: true,
      reserve: true,
      consumption: true
    };
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('kioskFunctions', JSON.stringify(kioskFunctions));
      } catch (error) {
        console.warn('Erro ao salvar configurações do totem:', error);
      }
    }
  }, [kioskFunctions]);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const wasKioskMode = window.localStorage.getItem('isKioskMode') === 'true';
        if (wasKioskMode) {
          setIsKioskMode(true);
          setKioskModalVisible(false);
        }
      } catch (error) {
        console.warn('Erro ao restaurar modo totem:', error);
      }
    }
  }, []);

  const clearPixIntervals = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }
  };

  const resetPixState = () => {
    setPixQrCode(null);
    setPixQrCodeBase64(null);
    setPixQrCodeUrl(null);
    setPixExpiration(null);
    setPixTimeLeft(null);
    setPixExpired(false);
    setPaymentStatus(null);
    setIsLoadingPix(false);
    setBuyerInfo(null);
    resetBuyerInfoForm();
    clearPixIntervals();
  };

  const toggleKioskFunction = (key) => {
    setKioskFunctions((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };

  const handleCloseKioskModal = () => {
    setKioskModalVisible(false);
    setKioskPassword('');
    setKioskPasswordError('');
  };

  const handleKioskPasswordSubmit = () => {
    if (!kioskPassword.trim()) {
      setKioskPasswordError('Informe a senha.');
      return false;
    }

    if (kioskPassword !== '1234') {
      setKioskPasswordError('Senha incorreta.');
      return false;
    }

    setKioskPasswordError('');
    return true;
  };

  const startPixCountdown = (expirationTimestamp) => {
    if (!expirationTimestamp) return;
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    const updateTime = () => {
      const diff = expirationTimestamp - Date.now();
      if (diff <= 0) {
        setPixTimeLeft('00:00');
        setPixExpired(true);
        setPaymentStatus((prev) => (prev === 'approved' ? prev : 'expired'));
        clearPixIntervals();
        return;
      }
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setPixTimeLeft(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };
    updateTime();
    countdownIntervalRef.current = setInterval(updateTime, 1000);
  };

  const startPixStatusPolling = (transactionId) => {
    if (!transactionId) return;
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
    }
    const poll = async () => {
      const statusData = await checkPaymentStatus(transactionId);
      if (!statusData) return;
      setPaymentStatus(statusData.status);
      if (statusData.status === 'approved') {
        clearPixIntervals();
        setChipTicketData((prev) => (prev ? { ...prev, status: statusData.status, approvalDate: statusData.dateApproved } : prev));
        setIsPixPaymentModalVisible(false);
        setIsBuyChipsModalVisible(false);
        setIsSuccessModalVisible(true);
        resetPixState();
      }
      if (statusData.status === 'rejected' || statusData.status === 'cancelled') {
        setPixExpired(true);
        clearPixIntervals();
        setChipTicketData((prev) => (prev ? { ...prev, status: statusData.status } : prev));
      }
    };
    poll();
    statusIntervalRef.current = setInterval(poll, 5000);
  };

  useEffect(() => {
    return () => {
      clearPixIntervals();
    };
  }, []);

  useEffect(() => {
    if (!isPixPaymentModalVisible) {
      clearPixIntervals();
    }
  }, [isPixPaymentModalVisible]);

  // Gerar PIX automaticamente quando o modal abrir
  useEffect(() => {
    const generatePix = async () => {
      if (isPixPaymentModalVisible && selectedChipsPackage && !pixQrCode && !isLoadingPix) {
        setIsLoadingPix(true);
        try {
          const buyerLabel = buyerInfo?.name ? ` para ${buyerInfo.name}` : '';
          const pixDescription = `Compra de ${selectedChipsPackage.amount} Saldo${buyerLabel} - VitorTable MT`;

          const pixData = await generatePixQRCode(
            selectedChipsPackage.price,
            pixDescription,
            {
              buyer: buyerInfo
            }
          );
          
          if (pixData && pixData.qrCode) {
            setPixQrCode(pixData.qrCode);
            setPixQrCodeBase64(pixData.qrCodeBase64 || null);
            setPixQrCodeUrl(pixData.qrCodeUrl || null);
            const expirationTimestamp = pixData.expirationDate ? new Date(pixData.expirationDate).getTime() : Date.now() + 15 * 60 * 1000;
            setPixExpiration(expirationTimestamp);
            setPixExpired(false);
            setPaymentStatus(pixData.status || 'pending');
            startPixCountdown(expirationTimestamp);
            startPixStatusPolling(pixData.transactionId);
            
            const ticketId = 'CHIP-' + Date.now().toString().slice(-8);
            const securityCode = Math.random().toString(36).substring(2, 8).toUpperCase();
            
            setChipTicketData({
              id: ticketId,
              amount: selectedChipsPackage.amount,
              price: selectedChipsPackage.price,
              date: new Date().toLocaleDateString('pt-BR'),
              time: new Date().toLocaleTimeString('pt-BR'),
              securityCode: securityCode,
              qrCode: pixData.qrCode,
              transactionId: pixData.transactionId,
              isMock: pixData.isMock,
              expirationDate: pixData.expirationDate,
              buyer: buyerInfo
            });
          } else {
            console.error('❌ PIX retornou null ou sem qrCode:', pixData);
            Alert.alert('Erro', 'Não foi possível gerar o QR Code PIX. Verifique suas credenciais do Mercado Pago.');
          }
        } catch (error) {
          console.error('❌ Erro ao gerar PIX:', error);
          console.error('Erro completo:', {
            message: error.message,
            stack: error.stack
          });
          Alert.alert('Erro', `Erro ao gerar QR Code PIX: ${error.message || 'Erro desconhecido'}`);
        } finally {
          setIsLoadingPix(false);
        }
      }
    };

    generatePix();
  }, [isPixPaymentModalVisible, selectedChipsPackage]);

  const { tables } = useTableContext();

  // Função para carregar reservas do localStorage
  const loadReservations = () => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = window.localStorage.getItem('reservations');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Erro ao carregar reservas:', error);
      return [];
    }
  };

  const selectedTables = useMemo(() => {
    if (!selectedTableIds.length) return [];
    return tables.filter((table) => selectedTableIds.includes(table.id));
  }, [selectedTableIds, tables]);

  const availableTables = useMemo(
    () => tables.filter((table) => table.status === 'fechada'),
    [tables]
  );

  const buyerDocumentOptions = useMemo(
    () => [
      { key: 'cpf', label: 'CPF' },
      { key: 'cnpj', label: 'CNPJ' },
      { key: 'rg', label: 'RG' },
      { key: 'certidao', label: 'Certidão Nascimento' },
      { key: 'habilitacao', label: 'Habilitação' }
    ],
    []
  );

  const buyerDocumentPlaceholder = useMemo(() => {
    switch (buyerDocumentType) {
      case 'cpf':
        return '000.000.000-00';
      case 'cnpj':
        return '00.000.000/0000-00';
      case 'rg':
        return 'RG do comprador';
      case 'certidao':
        return 'Número da certidão';
      case 'habilitacao':
        return 'Número da CNH';
      default:
        return 'Documento do comprador';
    }
  }, [buyerDocumentType]);

  const buyerDocumentKeyboardType = useMemo(() => {
    if (['cpf', 'cnpj', 'rg', 'habilitacao'].includes(buyerDocumentType)) {
      return 'numeric';
    }
    return 'default';
  }, [buyerDocumentType]);

  const handleOpenTable = () => {
    onNavigate('Login', { isKioskMode, kioskFunctions });
  };

  const handleOpenTables = () => {
    onNavigate('OpenTables', { isKioskMode, kioskFunctions });
  };

  const handleBuyChips = () => {
    setIsBuyChipsModalVisible(true);
    setSelectedChipsPackage(null);
    resetBuyerInfoForm();
  };

  const resetBuyerInfoForm = () => {
    setBuyerName('');
    setBuyerEmail('');
    setBuyerDocument('');
    setBuyerDocumentType('cpf');
    setBuyerInfoError('');
  };

  const handleOpenBuyerInfoModal = () => {
    if (!selectedChipsPackage) {
      Alert.alert('Atenção', 'Selecione um pacote antes de continuar.');
      return;
    }
    resetBuyerInfoForm();
    setBuyerInfoError('');
    setIsBuyChipsModalVisible(false);
    setIsBuyerInfoModalVisible(true);
  };

  const handleCloseBuyerInfoModal = () => {
    setIsBuyerInfoModalVisible(false);
  };

  const handleSelectBuyerDocumentType = (type) => {
    setBuyerDocumentType(type);
    setBuyerDocument('');
    setBuyerInfoError('');
  };

  const handleBuyerDocumentChange = (text) => {
    switch (buyerDocumentType) {
      case 'cpf':
        setBuyerDocument(formatCpf(text));
        break;
      case 'cnpj':
        setBuyerDocument(formatCnpj(text));
        break;
      case 'rg': {
        const digits = text.replace(/\D/g, '').slice(0, 12);
        setBuyerDocument(digits);
        break;
      }
      case 'habilitacao': {
        const digits = text.replace(/\D/g, '').slice(0, 11);
        setBuyerDocument(digits);
        break;
      }
      case 'certidao': {
        setBuyerDocument(text.toUpperCase().slice(0, 40));
        break;
      }
      default:
        setBuyerDocument(text);
    }
  };

  const validateEmail = (email) => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleBuyerInfoSubmit = () => {
    if (!buyerName.trim()) {
      setBuyerInfoError('Informe o nome do comprador.');
      return;
    }

    if (!buyerDocument.trim()) {
      setBuyerInfoError('Informe o documento do comprador.');
      return;
    }

    const digits = buyerDocument.replace(/\D/g, '');

    if (buyerDocumentType === 'cpf' && digits.length !== 11) {
      setBuyerInfoError('Informe um CPF válido (11 dígitos).');
      return;
    }

    if (buyerDocumentType === 'cnpj' && digits.length !== 14) {
      setBuyerInfoError('Informe um CNPJ válido (14 dígitos).');
      return;
    }

    if (buyerDocumentType === 'rg' && digits.length < 7) {
      setBuyerInfoError('Informe um RG válido.');
      return;
    }

    if (buyerDocumentType === 'certidao' && buyerDocument.length < 5) {
      setBuyerInfoError('Informe uma certidão válida.');
      return;
    }

    if (buyerDocumentType === 'habilitacao' && digits.length < 9) {
      setBuyerInfoError('Informe uma habilitação válida.');
      return;
    }

    if (!validateEmail(buyerEmail)) {
      setBuyerInfoError('Informe um e-mail válido ou deixe em branco.');
      return;
    }

    const collectedInfo = {
      name: buyerName.trim(),
      email: buyerEmail.trim(),
      document: buyerDocument.trim(),
      documentType: buyerDocumentType
    };

    setBuyerInfo(collectedInfo);
    setBuyerInfoError('');
    setIsBuyerInfoModalVisible(false);
    setIsPixPaymentModalVisible(true);
  };

  const handleOpenReserveModal = () => {
    setReserveModalVisible(true);
    setReserveFeedback('');
    setReserveError('');
  };

  const resetReserveForm = () => {
    setReserveName('');
    setReserveCpf('');
    setReserveAddress('');
    setReservePhone('');
    setReserveError('');
    setSelectedTableIds([]);
  };

  const handleCloseReserveModal = () => {
    setReserveModalVisible(false);
    resetReserveForm();
    setReserveFeedback('');
  };

  const handleConfirmReserve = () => {
    if (!reserveName || !reserveCpf || !reserveAddress || !reservePhone) {
      setReserveError('Preencha todos os campos para reservar.');
      setReserveFeedback('');
      return;
    }

    setReserveError('');
    setReserveFeedback('Dados recebidos! Escolha a mesa para concluir a reserva.');
    setReserveModalVisible(false);
    setSelectTableModalVisible(true);
  };

  const formatCpf = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const formatCnpj = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const applySearchMask = (value) => {
    if (!value) return '';

    const digits = value.replace(/\D/g, '');

    if (digits.length === 11) {
      return formatCpf(digits);
    }

    if (digits.length === 14) {
      return formatCnpj(digits);
    }

    return value;
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 10) {
      return digits
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  const formatTableNames = (info) => {
    if (!info) return 'Não selecionada';
    const numbers = info.tableNumbers;
    if (Array.isArray(numbers) && numbers.length > 0) {
      return numbers.map((mesa) => `Mesa ${mesa}`).join(', ');
    }
    const tablesList = info.tables;
    if (Array.isArray(tablesList) && tablesList.length > 0) {
      return tablesList.map((table) => `Mesa ${table.mesa}`).join(', ');
    }
    if (info.table) {
      return `Mesa ${info.table.mesa}`;
    }
    return 'Não selecionada';
  };

  const generateReservationText = (data = null) => {
    const reservationInfo = data || reservationData;
    if (!reservationInfo) {
      const date = new Date().toLocaleDateString('pt-BR');
      const time = new Date().toLocaleTimeString('pt-BR');
      const tableNames = formatTableNames({ tables: selectedTables });
      
      return `
*RESERVA DE MESA - VITOR TABLE MT*

📅 Data da Reserva: ${date} às ${time}

👤 *Dados do Cliente:*
Nome: ${reserveName}
CPF: ${reserveCpf}
Telefone: ${reservePhone}
Endereço: ${reserveAddress}

🍽️ *Informações da Reserva:*
Mesa(s): ${tableNames}

---
Reserva realizada via VitorTable MT
      `.trim();
    }
    
    const { client, date, time } = reservationInfo;
    const tableNames = formatTableNames(reservationInfo);
    
    return `
*RESERVA DE MESA - VITOR TABLE MT*

📅 Data da Reserva: ${date} às ${time}

👤 *Dados do Cliente:*
Nome: ${client.name}
CPF: ${client.cpf}
Telefone: ${client.phone}
Endereço: ${client.address}

🍽️ *Informações da Reserva:*
Mesa(s): ${tableNames}

---
Reserva realizada via VitorTable MT
    `.trim();
  };

  const handlePrintReservation = () => {
    if (!reservationData) return;
    
    if (typeof window !== 'undefined' && window.print) {
      const printWindow = window.open('', '', 'height=600,width=800');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Reserva de Mesa - Vitor Table MT</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            h1 {
              color: #F68648;
              text-align: center;
              border-bottom: 2px solid #F68648;
              padding-bottom: 10px;
            }
            .section {
              margin: 20px 0;
              padding: 15px;
              background-color: #f9f9f9;
              border-radius: 8px;
            }
            .section-title {
              font-weight: bold;
              color: #333;
              margin-bottom: 10px;
              font-size: 16px;
            }
            .info-row {
              margin: 8px 0;
              padding: 5px 0;
              border-bottom: 1px solid #e0e0e0;
            }
            .info-label {
              font-weight: bold;
              color: #666;
            }
            .info-value {
              color: #333;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              color: #999;
              font-size: 12px;
            }
            @media print {
              body { padding: 0; }
              .section { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>RESERVA DE MESA</h1>
          <div class="section">
            <div class="section-title">📅 Data da Reserva</div>
            <div class="info-row">
              <span class="info-label">Data:</span> <span class="info-value">${reservationData.date || ''}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Hora:</span> <span class="info-value">${reservationData.time || ''}</span>
            </div>
          </div>
          <div class="section">
            <div class="section-title">👤 Dados do Cliente</div>
            <div class="info-row">
              <span class="info-label">Nome:</span> <span class="info-value">${reservationData.client?.name || ''}</span>
            </div>
            <div class="info-row">
              <span class="info-label">CPF:</span> <span class="info-value">${reservationData.client?.cpf || ''}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Telefone:</span> <span class="info-value">${reservationData.client?.phone || ''}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Endereço:</span> <span class="info-value">${reservationData.client?.address || ''}</span>
            </div>
          </div>
          <div class="section">
            <div class="section-title">🍽️ Informações da Reserva</div>
            <div class="info-row">
              <span class="info-label">Mesa(s):</span> <span class="info-value">${formatTableNames(reservationData)}</span>
            </div>
          </div>
          <div class="footer">
            Reserva realizada via VitorTable MT
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    } else {
      Alert.alert('Impressão', 'Funcionalidade de impressão não disponível neste dispositivo.');
    }
  };

  const handleGeneratePDF = () => {
    handlePrintReservation();
  };

  const handleSendWhatsApp = () => {
    if (!reservationData) return;
    
    const reservationText = generateReservationText();
    const phoneNumber = '5545991046021';
    const encodedMessage = encodeURIComponent(reservationText);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    if (typeof window !== 'undefined') {
      window.open(whatsappUrl, '_blank');
    } else {
      Alert.alert('WhatsApp', 'Não foi possível abrir o WhatsApp.');
    }
  };

  const handleCloseReservationSuccess = () => {
    setIsReservationSuccessModalVisible(false);
    setReservationData(null);
    resetReserveForm();
  };

  const handleOpenConsumptionModal = () => {
    resetConsumptionForm();
    setConsumptionModalVisible(true);
  };

  const resetConsumptionForm = () => {
    setSearchQuery('');
    setFormError('');
    setConsumptionResults([]);
    setConsumptionMessage('');
  };

  const handleCloseConsumptionModal = () => {
    setConsumptionModalVisible(false);
    resetConsumptionForm();
  };

  const handleConfirmConsumption = () => {
    if (!searchQuery.trim()) {
      setFormError('Digite CPF, CNPJ, nome, e-mail, ID ou faça a leitura do QR Code.');
      return;
    }

    const filters = searchQuery.trim();

    const matches = loadConsumptionData(filters);
    if (matches.length === 0) {
      setConsumptionResults([]);
      setConsumptionMessage('Nenhum consumo encontrado para os dados informados.');
    } else {
      setConsumptionResults(matches);
      setConsumptionMessage('');
    }

    setFormError('');
  };

  const loadConsumptionData = (filters) => {
    const searchTerm = filters.toLowerCase();

    if (typeof window === 'undefined') {
      return [];
    }

    try {
      const storedReservations = window.localStorage.getItem('reservations');
      const reservations = storedReservations ? JSON.parse(storedReservations) : [];

      const storedTables = window.localStorage.getItem('cloe_tables_state_v1');
      const tablesData = storedTables ? JSON.parse(storedTables) : [];
      const tablesNormalized = Array.isArray(tablesData) ? tablesData : tablesData.tables || [];

      const allEntries = [
        ...reservations.map((reservation) => ({
          type: 'reserva',
          name: reservation.client?.name || '-',
          cpf: reservation.client?.cpf || '-',
          phone: reservation.client?.phone || '-',
          address: reservation.client?.address || '-',
          identifier: reservation.id,
          details: `Reserva para ${reservation.tableNumbers?.join(', ') || reservation.table?.mesa || '-'}`,
          timestamp: reservation.timestamp
        })),
        ...tablesNormalized.map((table) => ({
          type: 'mesa',
          name: table.cliente || '-',
          cpf: table.cpf || '-',
          phone: table.telefone || '-',
          address: table.email || '-',
          identifier: table.id,
          details: `Mesa ${table.mesa} - ${table.status}`,
          timestamp: table.chegada
        }))
      ];

      if (!searchTerm) return allEntries;

      return allEntries.filter((entry) => {
        const valuesToSearch = [
          entry.cpf,
          entry.name,
          entry.phone,
          entry.address,
          entry.identifier,
          entry.details
        ]
          .filter(Boolean)
          .map((value) => value.toString().toLowerCase());

        return valuesToSearch.some((value) => value.includes(searchTerm));
      });
    } catch (error) {
      console.warn('Erro ao buscar consumo:', error);
      return [];
    }
  };

  const generateConsumptionText = () => {
    const date = new Date().toLocaleDateString('pt-BR');
    const time = new Date().toLocaleTimeString('pt-BR');
    
    return `
*CONSUMO DE FICHAS - VITOR TABLE MT*

📅 Data da Consulta: ${date} às ${time}

🔍 *Dados da Busca:*
${searchCpf ? `CPF: ${searchCpf}` : ''}
${searchName ? `Nome: ${searchName}` : ''}
${searchId ? `ID: ${searchId}` : ''}

---
Consulta realizada via VitorTable MT
    `.trim();
  };

  const handlePrintConsumption = () => {
    const consumptionText = generateConsumptionText();
    if (typeof window !== 'undefined' && window.print) {
      const printWindow = window.open('', '', 'height=600,width=800');
      printWindow.document.write('<pre style="font-family: monospace; white-space: pre-wrap;">' + consumptionText + '</pre>');
      printWindow.document.close();
      printWindow.print();
    } else {
      Alert.alert('Impressão', 'Funcionalidade de impressão não disponível neste dispositivo.');
    }
  };

  const handleOpenKioskModal = () => {
    setKioskModalVisible(true);
    setKioskPassword('');
    setKioskPasswordError('');
  };

  const handleExitKioskMode = () => {
    setIsKioskMode(false);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('isKioskMode', 'false');
      } catch (error) {
        console.warn('Erro ao limpar estado do modo totem:', error);
      }
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.logoContainer}>
        <Text style={styles.logo}>🍽️</Text>
        <Text style={styles.title}>VitorTable MT</Text>
        <Text style={styles.subtitle}>Comanda Eletrônica</Text>
      </View>

      <View style={styles.buttonsContainer}>
        {(!isKioskMode || kioskFunctions.openTable) && (
          <PrimaryButton
            title="Abrir Mesa"
            onPress={handleOpenTable}
            style={styles.button}
          />
        )}
        
        {/* Linha 1: Mesas Abertas + Reserva de Mesa */}
        {((!isKioskMode || kioskFunctions.openTables) || (!isKioskMode || kioskFunctions.reserve)) && (
          <View style={styles.inlineButtons}>
            {(!isKioskMode || kioskFunctions.openTables) && (
              <PrimaryButton
                title="Mesas Abertas"
                onPress={handleOpenTables}
                style={[styles.smallButton, styles.secondaryButton]}
              />
            )}
            {(!isKioskMode || kioskFunctions.reserve) && (
              <PrimaryButton
                title="Reserva de Mesa"
                onPress={handleOpenReserveModal}
                style={[styles.smallButton, styles.orangeButton]}
                backgroundColor="#F97316"
              />
            )}
          </View>
        )}
        
        {/* Linha 2: Comprar Fichas + Ver Consumo */}
        {((!isKioskMode || kioskFunctions.buyChips) || (!isKioskMode || kioskFunctions.consumption)) && (
          <View style={styles.inlineButtons}>
            {(!isKioskMode || kioskFunctions.buyChips) && (
              <PrimaryButton
                title="Comprar Fichas"
                onPress={handleBuyChips}
                style={[styles.smallButton, styles.greenButton]}
                backgroundColor="#047857"
              />
            )}
            {(!isKioskMode || kioskFunctions.consumption) && (
              <PrimaryButton
                title="Ver Consumo"
                onPress={handleOpenConsumptionModal}
                style={[styles.smallButton, styles.yellowButton]}
                backgroundColor="#F59E0B"
              />
            )}
          </View>
        )}
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerVersion}>v1.0.0</Text>
        <Text style={styles.footerQuote}>As grandes ideias surgem da observacao dos pequenos detalhes.</Text>

        <View style={styles.contactSection}>
          <Text style={styles.contactTitle}>📞 Entre em Contato</Text>
          <Pressable onPress={handleOpenKioskModal}>
            <Text style={styles.contactCompany}>🚀 Rodrigo Dev MT</Text>
          </Pressable>
          
          <View style={styles.contactItem}>
            <Text style={styles.contactIcon}>📍</Text>
            <Text style={styles.contactLocation}>Mato Grosso, Brasil</Text>
          </View>
          
          <View style={styles.contactItem}>
            <Text style={styles.contactIcon}>📱</Text>
            <Text style={styles.contactPhone}>(66) 99225-8469  ·  (45) 99104-6021</Text>
          </View>
          
          <View style={styles.contactItem}>
            <Text style={styles.contactIcon}>📧</Text>
            <View style={styles.emailContainer}>
              <Text style={styles.contactEmail}>developer@rodrigodevmt.com.br</Text>
              <Text style={styles.contactEmail}>rodrigodev@yahoo.com</Text>
            </View>
          </View>
          
          <View style={styles.contactItem}>
            <Text style={styles.contactIcon}>🕒</Text>
            <View style={styles.scheduleContainer}>
              <Text style={styles.contactSchedule}>Segunda - Sexta: 8h às 18h</Text>
              <Text style={styles.contactSchedule}>Sábado: 8h às 12h</Text>
            </View>
          </View>
        </View>
      </View>

      <Modal
        visible={isConsumptionModalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleCloseConsumptionModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Buscar Consumo de Fichas</Text>
            <Text style={styles.modalSubtitle}>Informe pelo menos um dos campos abaixo</Text>

            <View style={styles.modalField}>
              <Text style={styles.modalLabel}>Buscar por CPF, CNPJ, Nome, E-mail, ID ou Código</Text>
              <TextInput
                style={styles.modalInput}
                placeholder="Digite ou escaneie o QR Code"
                value={searchQuery}
                onChangeText={(text) => setSearchQuery(applySearchMask(text))}
              />
            </View>

            <Pressable style={styles.qrReadButton}>
              <Text style={styles.qrReadButtonText}>📷 Ler QR Code (em breve)</Text>
            </Pressable>

            {formError ? <Text style={styles.modalError}>{formError}</Text> : null}

            {consumptionMessage ? <Text style={styles.modalInfo}>{consumptionMessage}</Text> : null}

            {consumptionResults.length > 0 && (
              <View style={styles.consumptionResults}>
                {consumptionResults.map((item) => (
                  <View key={`${item.type}-${item.identifier}`} style={styles.consumptionResultItem}>
                    <Text style={styles.consumptionResultTitle}>{item.type === 'mesa' ? 'Mesa Aberta' : 'Reserva'}</Text>
                    <Text style={styles.consumptionResultText}>Nome: {item.name}</Text>
                    <Text style={styles.consumptionResultText}>CPF: {item.cpf}</Text>
                    <Text style={styles.consumptionResultText}>Contato: {item.phone}</Text>
                    <Text style={styles.consumptionResultText}>Info: {item.details}</Text>
                  </View>
                ))}
              </View>
            )}

            <View style={styles.modalActions}>
              <Pressable style={[styles.modalButton, styles.modalButtonSecondary]} onPress={handleCloseConsumptionModal}>
                <Text style={styles.modalButtonTextSecondary}>Cancelar</Text>
              </Pressable>
              <Pressable style={[styles.modalButton, styles.modalButtonPrimary]} onPress={handleConfirmConsumption}>
                <Text style={styles.modalButtonTextPrimary}>Buscar</Text>
              </Pressable>
            </View>

            <Pressable
              style={[styles.actionButton, styles.actionButtonPrint]}
              onPress={handlePrintConsumption}
            >
              <Text style={styles.actionButtonText}>🖨️ Imprimir Consumo</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal
        visible={isReserveModalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleCloseReserveModal}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContainer, styles.reserveContainer]}>
            <Text style={styles.modalTitle}>Reserva de Mesa</Text>
            <Text style={styles.modalSubtitle}>Informe os dados do cliente</Text>

            <ScrollView
              style={styles.reserveScroll}
              contentContainerStyle={styles.reserveContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Nome Completo</Text>
                <TextInput
                  style={styles.modalInput}
                  placeholder="Ex: Maria Silva"
                  value={reserveName}
                  onChangeText={setReserveName}
                />
              </View>

              <View style={styles.modalInlineFields}>
                <View style={[styles.modalField, styles.modalFieldHalf]}>
                  <Text style={styles.modalLabel}>CPF</Text>
                  <TextInput
                    style={[styles.modalInput, styles.modalInputMasked]}
                    placeholder="000.000.000-00"
                    value={reserveCpf}
                    onChangeText={(text) => setReserveCpf(formatCpf(text))}
                    keyboardType="numeric"
                  />
                </View>

                <View style={[styles.modalField, styles.modalFieldHalf]}>
                  <Text style={styles.modalLabel}>Telefone</Text>
                  <TextInput
                    style={[styles.modalInput, styles.modalInputMasked]}
                    placeholder="(66) 90000-0000"
                    value={reservePhone}
                    onChangeText={(text) => setReservePhone(formatPhone(text))}
                    keyboardType="phone-pad"
                  />
                </View>
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Endereço</Text>
                <TextInput
                  style={styles.modalInput}
                  placeholder="Rua, nº, bairro"
                  value={reserveAddress}
                  onChangeText={setReserveAddress}
                />
              </View>

              {reserveError ? <Text style={styles.modalError}>{reserveError}</Text> : null}
            </ScrollView>

            <View style={styles.modalActions}>
              <Pressable style={[styles.modalButton, styles.modalButtonSecondary]} onPress={handleCloseReserveModal}>
                <Text style={styles.modalButtonTextSecondary}>Cancelar</Text>
              </Pressable>
              <Pressable style={[styles.modalButton, styles.modalButtonPrimary]} onPress={handleConfirmReserve}>
                <Text style={styles.modalButtonTextPrimary}>Confirmar Reserva</Text>
              </Pressable>
            </View>
            {reserveFeedback ? <Text style={styles.modalInfo}>{reserveFeedback}</Text> : null}
          </View>
        </View>
      </Modal>

      <Modal
        visible={isSelectTableModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setSelectTableModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContainer, styles.reserveContainer]}>
            <Text style={styles.modalTitle}>Selecionar Mesa</Text>
            <Text style={styles.modalSubtitle}>Escolha uma mesa para concluir a reserva</Text>

            <ScrollView contentContainerStyle={styles.reserveContent}>
              {availableTables.length > 0 ? (
                availableTables.map((table) => (
                  <Pressable
                    key={table.id}
                    style={[
                      styles.tableOption,
                      selectedTableIds.includes(table.id) && styles.tableOptionSelected
                    ]}
                    onPress={() => {
                      setSelectedTableIds((prev) => {
                        if (prev.includes(table.id)) {
                          return prev.filter((id) => id !== table.id);
                        }
                        return [...prev, table.id];
                      });
                    }}
                  >
                    <Text style={styles.tableOptionTitle}>Mesa {table.mesa}</Text>
                    <Text style={styles.tableOptionInfo}>Capacidade: {table.pessoas} pessoa(s)</Text>
                    <Text style={[styles.tableOptionInfo, styles.tableAvailableStatus]}>✓ Disponível</Text>
                  </Pressable>
                ))
              ) : (
                <Text style={styles.modalInfo}>Nenhuma mesa disponível no momento.</Text>
              )}
            </ScrollView>

            <View style={styles.modalActions}>
              <Pressable style={[styles.modalButton, styles.modalButtonSecondary]} onPress={() => {
                setSelectTableModalVisible(false);
                setReserveFeedback('');
              }}>
                <Text style={styles.modalButtonTextSecondary}>Voltar</Text>
              </Pressable>
              <Pressable
                style={[styles.modalButton, styles.modalButtonPrimary, !selectedTableIds.length && styles.modalButtonDisabled]}
                onPress={() => {
                  if (!selectedTableIds.length) return;
                  const selectedTablesList = tables.filter((t) => selectedTableIds.includes(t.id));
                  const now = new Date();
                  const reservationInfo = {
                    tables: selectedTablesList,
                    tableNumbers: selectedTablesList.map((table) => table.mesa),
                    table: selectedTablesList[0] || null,
                    client: {
                      name: reserveName,
                      cpf: reserveCpf,
                      phone: reservePhone,
                      address: reserveAddress
                    },
                    date: now.toLocaleDateString('pt-BR'),
                    time: now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
                    timestamp: now.toISOString()
                  };
                  setReservationData(reservationInfo);
                  // Salvar reserva no localStorage
                  const savedReservations = loadReservations();
                  const newReservation = {
                    ...reservationInfo,
                    id: Date.now().toString()
                  };
                  savedReservations.push(newReservation);
                  if (typeof window !== 'undefined') {
                    window.localStorage.setItem('reservations', JSON.stringify(savedReservations));
                  }
                  setSelectTableModalVisible(false);
                  setIsReservationSuccessModalVisible(true);
                }}
              >
                <Text style={styles.modalButtonTextPrimary}>Confirmar Mesa</Text>
              </Pressable>
            </View>

            <View style={styles.reserveActionButtons}>
              <Pressable
                style={[styles.actionButton, styles.actionButtonPrint]}
                onPress={handlePrintReservation}
              >
                <Text style={styles.actionButtonText}>🖨️ Imprimir</Text>
              </Pressable>
              <Pressable
                style={[styles.actionButton, styles.actionButtonWhatsApp]}
                onPress={handleSendWhatsApp}
              >
                <Text style={styles.actionButtonText}>💬 WhatsApp</Text>
              </Pressable>
            </View>
            {reserveFeedback && !isReserveModalVisible ? (
              <Text style={styles.modalSuccess}>{reserveFeedback}</Text>
            ) : null}
          </View>
        </View>
      </Modal>

      {/* Modal de Sucesso da Reserva */}
      <Modal
        visible={isReservationSuccessModalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleCloseReservationSuccess}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContainer, styles.reservationSuccessContainer]}>
            <Text style={styles.successTitle}>✅ Reserva Confirmada!</Text>
            <Text style={styles.successSubtitle}>Mesa reservada com sucesso</Text>

            {reservationData && (
              <ScrollView style={styles.reservationDetails} contentContainerStyle={styles.reservationDetailsContent}>
                {/* Data e Hora */}
                <View style={styles.reservationSection}>
                  <Text style={styles.reservationSectionTitle}>📅 Data da Reserva</Text>
                  <View style={styles.reservationInfoRow}>
                    <Text style={styles.reservationInfoLabel}>Data:</Text>
                    <Text style={styles.reservationInfoValue}>{reservationData.date}</Text>
                  </View>
                  <View style={styles.reservationInfoRow}>
                    <Text style={styles.reservationInfoLabel}>Hora:</Text>
                    <Text style={styles.reservationInfoValue}>{reservationData.time}</Text>
                  </View>
                </View>

                {/* Dados do Cliente */}
                <View style={styles.reservationSection}>
                  <Text style={styles.reservationSectionTitle}>👤 Dados do Cliente</Text>
                  <View style={styles.reservationInfoRow}>
                    <Text style={styles.reservationInfoLabel}>Nome:</Text>
                    <Text style={styles.reservationInfoValue}>{reservationData.client?.name}</Text>
                  </View>
                  <View style={styles.reservationInfoRow}>
                    <Text style={styles.reservationInfoLabel}>CPF:</Text>
                    <Text style={styles.reservationInfoValue}>{reservationData.client?.cpf}</Text>
                  </View>
                  <View style={styles.reservationInfoRow}>
                    <Text style={styles.reservationInfoLabel}>Telefone:</Text>
                    <Text style={styles.reservationInfoValue}>{reservationData.client?.phone}</Text>
                  </View>
                  <View style={styles.reservationInfoRow}>
                    <Text style={styles.reservationInfoLabel}>Endereço:</Text>
                    <Text style={styles.reservationInfoValue}>{reservationData.client?.address}</Text>
                  </View>
                </View>

                {/* Informações da Reserva */}
                <View style={styles.reservationSection}>
                  <Text style={styles.reservationSectionTitle}>🍽️ Informações da Reserva</Text>
                  <View style={styles.reservationInfoRow}>
                    <Text style={styles.reservationInfoLabel}>Mesa(s):</Text>
                    <Text style={styles.reservationInfoValue}>
                      {formatTableNames(reservationData)}
                    </Text>
                  </View>
                </View>
              </ScrollView>
            )}

            {/* Botões de Ação */}
            <View style={styles.reservationActionButtons}>
              <Pressable
                style={[styles.reservationActionButton, styles.reservationActionButtonWhatsApp]}
                onPress={handleSendWhatsApp}
              >
                <Text style={styles.reservationActionButtonText}>💬 WhatsApp</Text>
              </Pressable>
              <Pressable
                style={[styles.reservationActionButton, styles.reservationActionButtonPrint]}
                onPress={handlePrintReservation}
              >
                <Text style={[styles.reservationActionButtonText, styles.reservationActionButtonPrintText]}>🖨️ Imprimir</Text>
              </Pressable>
              <Pressable
                style={[styles.reservationActionButton, styles.reservationActionButtonPDF]}
                onPress={handleGeneratePDF}
              >
                <Text style={styles.reservationActionButtonText}>📄 PDF</Text>
              </Pressable>
            </View>

            <View style={styles.modalActions}>
              <Pressable
                style={[styles.modalButton, styles.modalButtonPrimary]}
                onPress={handleCloseReservationSuccess}
              >
                <Text style={styles.modalButtonTextPrimary}>Fechar</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={isBuyChipsModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsBuyChipsModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Comprar Saldo</Text>
            <Text style={styles.modalSubtitle}>Selecione um pacote</Text>

            <View style={styles.chipsRow}>
              <Pressable
                style={[styles.chipsPackage, styles.chipsPackageHalf, selectedChipsPackage?.id === '1' && styles.chipsPackageSelected]}
                onPress={() => setSelectedChipsPackage({ id: '1', amount: 50, price: 2.50 })}
              >
                <Text style={styles.chipsPackageText}>50 Saldo</Text>
                <Text style={styles.chipsPackagePrice}>R$ 2,50</Text>
              </Pressable>
              <Pressable
                style={[styles.chipsPackage, styles.chipsPackageHalf, selectedChipsPackage?.id === '2' && styles.chipsPackageSelected]}
                onPress={() => setSelectedChipsPackage({ id: '2', amount: 100, price: 5.00 })}
              >
                <View style={styles.popularBadge}>
                  <Text style={styles.popularText}>Popular</Text>
                </View>
                <Text style={styles.chipsPackageText}>100 Saldo</Text>
                <Text style={styles.chipsPackagePrice}>R$ 5,00</Text>
              </Pressable>
            </View>

            <View style={styles.chipsRow}>
              <Pressable
                style={[styles.chipsPackage, styles.chipsPackageHalf, selectedChipsPackage?.id === '3' && styles.chipsPackageSelected]}
                onPress={() => setSelectedChipsPackage({ id: '3', amount: 200, price: 10.00 })}
              >
                <Text style={styles.chipsPackageText}>200 Saldo</Text>
                <Text style={styles.chipsPackagePrice}>R$ 10,00</Text>
              </Pressable>
              <Pressable
                style={[styles.chipsPackage, styles.chipsPackageHalf, selectedChipsPackage?.id === '4' && styles.chipsPackageSelected]}
                onPress={() => setSelectedChipsPackage({ id: '4', amount: 500, price: 25.00 })}
              >
                <Text style={styles.chipsPackageText}>500 Saldo</Text>
                <Text style={styles.chipsPackagePrice}>R$ 25,00</Text>
              </Pressable>
            </View>

            <View style={styles.chipsRow}>
              <Pressable
                style={[styles.chipsPackage, styles.chipsPackageHalf, selectedChipsPackage?.id === '5' && styles.chipsPackageSelected]}
                onPress={() => setSelectedChipsPackage({ id: '5', amount: 1000, price: 50.00 })}
              >
                <Text style={styles.chipsPackageText}>1000 Saldo</Text>
                <Text style={styles.chipsPackagePrice}>R$ 50,00</Text>
              </Pressable>
              <Pressable
                style={[styles.chipsPackage, styles.chipsPackageHalf, selectedChipsPackage?.id === '6' && styles.chipsPackageSelected]}
                onPress={() => setSelectedChipsPackage({ id: '6', amount: 2000, price: 100.00 })}
              >
                <Text style={styles.chipsPackageText}>2000 Saldo</Text>
                <Text style={styles.chipsPackagePrice}>R$ 100,00</Text>
              </Pressable>
            </View>

            <View style={styles.modalActions}>
              <Pressable style={[styles.modalButton, styles.modalButtonSecondary]} onPress={() => setIsBuyChipsModalVisible(false)}>
                <Text style={styles.modalButtonTextSecondary}>Cancelar</Text>
              </Pressable>
              <Pressable
                style={[styles.modalButton, styles.modalButtonPrimary, !selectedChipsPackage && styles.modalButtonDisabled]}
                onPress={() => {
                  if (selectedChipsPackage) {
                    handleOpenBuyerInfoModal();
                  }
                }}
              >
                <Text style={styles.modalButtonTextPrimary}>Comprar</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={isBuyerInfoModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setIsBuyerInfoModalVisible(false);
          setIsBuyChipsModalVisible(true);
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContainer, styles.buyerInfoContainer]}>
            <Text style={styles.modalTitle}>Dados do Comprador</Text>
            <Text style={styles.modalSubtitle}>Informe quem realizará a compra antes de gerar o PIX</Text>

            <View style={styles.modalField}>
              <Text style={styles.modalLabel}>Nome Completo</Text>
              <TextInput
                style={styles.modalInput}
                placeholder="Ex: Maria Silva"
                value={buyerName}
                onChangeText={setBuyerName}
              />
            </View>

            <View style={styles.modalField}>
              <Text style={styles.modalLabel}>E-mail (opcional)</Text>
              <TextInput
                style={styles.modalInput}
                placeholder="exemplo@email.com"
                keyboardType="email-address"
                autoCapitalize="none"
                value={buyerEmail}
                onChangeText={setBuyerEmail}
              />
            </View>

            <View style={styles.modalField}>
              <Text style={styles.modalLabel}>Documento</Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.documentTypeRow}
              >
                {buyerDocumentOptions.map((option) => (
                  <Pressable
                    key={option.key}
                    style={[styles.documentTypeChip, buyerDocumentType === option.key && styles.documentTypeChipActive]}
                    onPress={() => handleSelectBuyerDocumentType(option.key)}
                  >
                    <Text style={[styles.documentTypeChipText, buyerDocumentType === option.key && styles.documentTypeChipTextActive]}>
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </ScrollView>
              <TextInput
                style={styles.modalInput}
                placeholder={buyerDocumentPlaceholder}
                keyboardType={buyerDocumentKeyboardType}
                value={buyerDocument}
                onChangeText={handleBuyerDocumentChange}
              />
            </View>

            {buyerInfoError ? <Text style={styles.modalError}>{buyerInfoError}</Text> : null}

            <View style={styles.modalActions}>
              <Pressable
                style={[styles.modalButton, styles.modalButtonSecondary]}
                onPress={() => {
                  setIsBuyerInfoModalVisible(false);
                  setIsBuyChipsModalVisible(true);
                }}
              >
                <Text style={styles.modalButtonTextSecondary}>Voltar</Text>
              </Pressable>
              <Pressable
                style={[styles.modalButton, styles.modalButtonPrimary]}
                onPress={handleBuyerInfoSubmit}
              >
                <Text style={styles.modalButtonTextPrimary}>Avançar</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={isPixPaymentModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsPixPaymentModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Pagamento PIX</Text>
            <Text style={styles.modalSubtitle}>Escaneie o QR Code para pagar</Text>

            <View style={styles.pixQrContainer}>
              {/* Coluna da Esquerda - QR Code */}
              <View style={styles.pixQrColumn}>
                {isLoadingPix ? (
                  <View style={styles.pixQrBox}>
                    <Text style={styles.pixQrIcon}>⏳</Text>
                    <Text style={styles.pixQrText}>Gerando QR Code...</Text>
                    <Text style={styles.pixQrSubtext}>Aguarde um momento</Text>
                  </View>
                ) : pixQrCode ? (
                  <View style={styles.pixQrContent}>
                    <PixQRCode
                      qrCode={pixQrCode}
                      qrCodeBase64={pixQrCodeBase64}
                      qrCodeUrl={pixQrCodeUrl}
                      transactionId={chipTicketData?.transactionId}
                      amount={selectedChipsPackage?.price}
                    />
                    {pixTimeLeft && !pixExpired && (
                      <Text style={styles.pixCountdown}>Expira em {pixTimeLeft}</Text>
                    )}
                    {pixExpired && (
                      <Text style={styles.pixExpiredText}>Pagamento expirado. Gere novamente.</Text>
                    )}
                    {paymentStatus && (
                      <Text style={styles.pixStatusText}>Status: {paymentStatus}</Text>
                    )}
                    {chipTicketData?.isMock && (
                      <Text style={styles.pixMockWarning}>⚠️ Modo Teste</Text>
                    )}
                  </View>
                ) : (
                  <View style={styles.pixQrBox}>
                    <Text style={styles.pixQrIcon}>📱</Text>
                    <Text style={styles.pixQrText}>Preparando...</Text>
                  </View>
                )}
              </View>

              {/* Coluna da Direita - Informações e Código PIX */}
              <View style={styles.pixQrColumn}>
                <View style={styles.pixInfoContainer}>
                  <Text style={styles.pixLabel}>Valor:</Text>
                  <Text style={[styles.pixValue, { fontSize: 18, fontWeight: 'bold' }]}>R$ {selectedChipsPackage?.price.toFixed(2)}</Text>
                  <Text style={styles.pixLabel}>Saldo em Fichas:</Text>
                  <Text style={[styles.pixValue, { fontSize: 18, fontWeight: 'bold' }]}>{selectedChipsPackage?.amount}</Text>
                  {buyerInfo?.name ? (
                    <>
                      <Text style={[styles.pixLabel, { marginTop: 8 }]}>Comprador:</Text>
                      <Text style={styles.pixValue}>{buyerInfo.name}</Text>
                      {buyerInfo.document ? (
                        <Text style={styles.pixValue}>
                          {buyerInfo.documentType?.toUpperCase()}: {buyerInfo.document}
                        </Text>
                      ) : null}
                      {buyerInfo.email ? (
                        <Text style={styles.pixValue}>{buyerInfo.email}</Text>
                      ) : null}
                    </>
                  ) : null}
                  <Text style={[styles.pixLabel, { marginTop: 8 }]}>ID da Transação:</Text>
                  <Text style={styles.pixValue}>{chipTicketData?.transactionId || '--'}</Text>
                </View>

                {pixQrCode && (
                  <View style={styles.pixCodeContainer}>
                    <Text style={styles.pixCodeLabel}>Código PIX (Copia e Cola)</Text>
                    <View style={styles.pixCodeBox}>
                      <Text style={styles.pixCodeText} numberOfLines={2}>
                        {pixQrCode.substring(0, 60)}...
                      </Text>
                    </View>
                    <Pressable 
                      style={styles.pixCopyButton}
                      onPress={() => {
                        if (navigator.clipboard) {
                          navigator.clipboard.writeText(pixQrCode);
                          Alert.alert('Sucesso', 'Código PIX copiado!');
                        }
                      }}
                    >
                      <Text style={styles.pixCopyButtonText}>📋 Copiar Código</Text>
                    </Pressable>
                  </View>
                )}
              </View>
            </View>

            <View style={styles.modalActions}>
              <Pressable 
                style={[styles.modalButton, styles.modalButtonSecondary]} 
                onPress={() => {
                  setIsPixPaymentModalVisible(false);
                  resetPixState();
                }}
              >
                <Text style={styles.modalButtonTextSecondary}>Cancelar</Text>
              </Pressable>
              <Pressable
                style={[styles.modalButton, styles.modalButtonPrimary, (isLoadingPix || !pixQrCode || paymentStatus !== 'approved') && styles.modalButtonDisabled]}
                onPress={() => {
                  if (pixQrCode && chipTicketData) {
                    setIsPixPaymentModalVisible(false);
                    setIsBuyChipsModalVisible(false);
                    setIsSuccessModalVisible(true);
                    resetPixState();
                  }
                }}
              >
                <Text style={styles.modalButtonTextPrimary}>
                  {paymentStatus === 'approved' ? 'Confirmar Pagamento' : 'Aguardando pagamento'}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={isSuccessModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsSuccessModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.successIcon}>✓</Text>
            <Text style={styles.modalTitle}>Compra Realizada!</Text>
            <Text style={styles.modalSubtitle}>Ficha de Crédito Gerada</Text>
            <View style={styles.ticketContainer}>
              <View style={styles.ticketHeader}>
                <Text style={styles.ticketTitle}>VITOR TABLE MT</Text>
                <Text style={styles.ticketSubtitle}>Ficha de Crédito</Text>
              </View>
              {chipTicketData && (
                <ScrollView style={styles.ticketDetails} contentContainerStyle={styles.ticketDetailsContent}>
                  <View style={styles.ticketRow}>
                    <Text style={styles.ticketLabel}>ID:</Text>
                    <Text style={styles.ticketValue}>{chipTicketData?.id}</Text>
                  </View>
                  <View style={styles.ticketRow}>
                    <Text style={styles.ticketLabel}>Fichas:</Text>
                    <Text style={styles.ticketValue}>{chipTicketData?.amount}</Text>
                  </View>
                  <View style={styles.ticketRow}>
                    <Text style={styles.ticketLabel}>Valor:</Text>
                    <Text style={styles.ticketValue}>R$ {chipTicketData?.price.toFixed(2)}</Text>
                  </View>
                  {chipTicketData?.buyer?.name ? (
                    <View style={styles.ticketRow}>
                      <Text style={styles.ticketLabel}>Comprador:</Text>
                      <Text style={styles.ticketValue}>{chipTicketData.buyer.name}</Text>
                    </View>
                  ) : null}
                  {chipTicketData?.buyer?.document ? (
                    <View style={styles.ticketRow}>
                      <Text style={styles.ticketLabel}>{chipTicketData.buyer.documentType?.toUpperCase()}:</Text>
                      <Text style={styles.ticketValue}>{chipTicketData.buyer.document}</Text>
                    </View>
                  ) : null}
                  {chipTicketData?.buyer?.email ? (
                    <View style={styles.ticketRow}>
                      <Text style={styles.ticketLabel}>E-mail:</Text>
                      <Text style={styles.ticketValue}>{chipTicketData.buyer.email}</Text>
                    </View>
                  ) : null}
                  <View style={styles.ticketRow}>
                    <Text style={styles.ticketLabel}>Data:</Text>
                    <Text style={styles.ticketValue}>{chipTicketData?.date}</Text>
                  </View>
                  <View style={styles.ticketRow}>
                    <Text style={styles.ticketLabel}>Hora:</Text>
                    <Text style={styles.ticketValue}>{chipTicketData?.time}</Text>
                  </View>
                  <View style={styles.ticketRow}>
                    <Text style={styles.ticketLabel}>Segurança:</Text>
                    <Text style={styles.ticketValue}>{chipTicketData?.securityCode}</Text>
                  </View>
                </ScrollView>
              )}

              <View style={styles.ticketQrContainer}>
                <Text style={styles.ticketQrLabel}>QR Code para Recarga e Consulta</Text>
                <View style={styles.ticketQrBox}>
                  <Text style={styles.ticketQrText}>📱 QR</Text>
                  <Text style={styles.ticketQrUrl}>{chipTicketData?.qrCode}</Text>
                </View>
              </View>

              <View style={styles.ticketFooter}>
                <Text style={styles.ticketFooterText}>Escaneie para consultar saldo e consumo</Text>
              </View>
            </View>

            <View style={styles.modalActions}>
              <Pressable
                style={[styles.modalButton, styles.modalButtonPrimary]}
                onPress={() => {
                  const printContent = `
                    VITOR TABLE MT
                    Ficha de Crédito

                    ID: ${chipTicketData?.id}
                    Fichas: ${chipTicketData?.amount}
                    Valor: R$ ${chipTicketData?.price.toFixed(2)}
                    ${chipTicketData?.buyer?.name ? `Comprador: ${chipTicketData.buyer.name}` : ''}
                    ${chipTicketData?.buyer?.document ? `${chipTicketData.buyer.documentType?.toUpperCase()}: ${chipTicketData.buyer.document}` : ''}
                    ${chipTicketData?.buyer?.email ? `Email: ${chipTicketData.buyer.email}` : ''}
                    Data: ${chipTicketData?.date}
                    Hora: ${chipTicketData?.time}
                    Segurança: ${chipTicketData?.securityCode}

                    QR Code: ${chipTicketData?.qrCode}
                    
                    Escaneie para consultar saldo e consumo
                  `;
                  if (typeof window !== 'undefined' && window.print) {
                    const printWindow = window.open('', '', 'height=600,width=400');
                    printWindow.document.write('<pre style="font-family: monospace; white-space: pre-wrap;">' + printContent + '</pre>');
                    printWindow.document.close();
                    printWindow.print();
                  }
                }}
              >
                <Text style={styles.modalButtonTextPrimary}>🖨️ Imprimir Ficha</Text>
              </Pressable>
              <Pressable
                style={[styles.modalButton, styles.modalButtonSecondary]}
                onPress={() => {
                  setIsSuccessModalVisible(false);
                  setChipTicketData(null);
                }}
              >
                <Text style={styles.modalButtonTextSecondary}>Fechar</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={isKioskModalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleCloseKioskModal}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContainer, styles.kioskPasswordContainer]}>
            <Text style={styles.modalTitle}>Modo Totem</Text>
            <View style={styles.kioskStatusContainer}>
              <View style={[styles.kioskStatusBadge, isKioskMode ? styles.kioskStatusActive : styles.kioskStatusInactive]}>
                <Text style={styles.kioskStatusText}>
                  {isKioskMode ? '● ATIVO' : '○ INATIVO'}
                </Text>
              </View>
            </View>
            <Text style={styles.modalSubtitle}>Digite a senha para {isKioskMode ? 'desativar' : 'ativar'}</Text>

            <View style={styles.modalField}>
              <Text style={styles.modalLabel}>Senha</Text>
              <TextInput
                style={styles.modalInput}
                placeholder="Digite a senha"
                value={kioskPassword}
                onChangeText={setKioskPassword}
                secureTextEntry
              />
            </View>

            {kioskPasswordError ? <Text style={styles.modalError}>{kioskPasswordError}</Text> : null}

            <View style={styles.kioskFunctionsContainer}>
              <Text style={styles.kioskSectionTitle}>Funções Ativas</Text>

              <View style={styles.kioskFunctionsRow}>
                <Pressable
                  style={[styles.kioskFunctionItem, styles.kioskFunctionItemHalf, kioskFunctions.openTable && styles.kioskFunctionActive]}
                  onPress={() => toggleKioskFunction('openTable')}
                >
                  <View style={styles.kioskCheckbox}>
                    {kioskFunctions.openTable && <Text style={styles.kioskCheckmark}>✓</Text>}
                  </View>
                  <Text style={styles.kioskFunctionText}>Abrir Mesa</Text>
                </Pressable>

                <Pressable
                  style={[styles.kioskFunctionItem, styles.kioskFunctionItemHalf, kioskFunctions.openTables && styles.kioskFunctionActive]}
                  onPress={() => toggleKioskFunction('openTables')}
                >
                  <View style={styles.kioskCheckbox}>
                    {kioskFunctions.openTables && <Text style={styles.kioskCheckmark}>✓</Text>}
                  </View>
                  <Text style={styles.kioskFunctionText}>Mesas Abertas</Text>
                </Pressable>
              </View>

              <View style={styles.kioskFunctionsRow}>
                <Pressable
                  style={[styles.kioskFunctionItem, styles.kioskFunctionItemHalf, kioskFunctions.buyChips && styles.kioskFunctionActive]}
                  onPress={() => toggleKioskFunction('buyChips')}
                >
                  <View style={styles.kioskCheckbox}>
                    {kioskFunctions.buyChips && <Text style={styles.kioskCheckmark}>✓</Text>}
                  </View>
                  <Text style={styles.kioskFunctionText}>Comprar Fichas</Text>
                </Pressable>

                <Pressable
                  style={[styles.kioskFunctionItem, styles.kioskFunctionItemHalf, kioskFunctions.reserve && styles.kioskFunctionActive]}
                  onPress={() => toggleKioskFunction('reserve')}
                >
                  <View style={styles.kioskCheckbox}>
                    {kioskFunctions.reserve && <Text style={styles.kioskCheckmark}>✓</Text>}
                  </View>
                  <Text style={styles.kioskFunctionText}>Reserva de Mesa</Text>
                </Pressable>
              </View>

              <View style={styles.kioskFunctionsRow}>
                <Pressable
                  style={[styles.kioskFunctionItem, styles.kioskFunctionItemHalf, kioskFunctions.consumption && styles.kioskFunctionActive]}
                  onPress={() => toggleKioskFunction('consumption')}
                >
                  <View style={styles.kioskCheckbox}>
                    {kioskFunctions.consumption && <Text style={styles.kioskCheckmark}>✓</Text>}
                  </View>
                  <Text style={styles.kioskFunctionText}>Ver Consumo</Text>
                </Pressable>
              </View>
            </View>

            <View style={styles.modalActions}>
              <Pressable style={[styles.modalButton, styles.modalButtonSecondary]} onPress={handleCloseKioskModal}>
                <Text style={styles.modalButtonTextSecondary}>Cancelar</Text>
              </Pressable>
              <Pressable style={[styles.modalButton, styles.modalButtonPrimary]} onPress={() => {
                const isValidPassword = handleKioskPasswordSubmit();
                if (!isValidPassword) {
                  return;
                }

                const newMode = !isKioskMode;
                setIsKioskMode(newMode);
                handleCloseKioskModal();

                if (typeof window !== 'undefined') {
                  try {
                    window.localStorage.setItem('isKioskMode', newMode ? 'true' : 'false');
                  } catch (error) {
                    console.warn('Erro ao salvar estado do modo totem:', error);
                  }
                }
              }}>
                <Text style={styles.modalButtonTextPrimary}>{isKioskMode ? 'Desativar' : 'Ativar'} Totem</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 0
  },
  logoContainer: {
    alignItems: 'center',
    marginTop: 40,
    marginBottom: 20
  },
  logo: {
    fontSize: 80,
    marginBottom: 16
  },
  title: {
    fontSize: 36,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 8
  },
  subtitle: {
    fontSize: 16,
    color: colors.muted,
    fontWeight: '500'
  },
  buttonsContainer: {
    width: '100%',
    maxWidth: 320,
    gap: 16,
    marginBottom: 20
  },
  button: {
    marginBottom: 0
  },
  secondaryButton: {
    backgroundColor: colors.accent
  },
  tertiaryButton: {
    backgroundColor: '#6B7280'
  },
  inlineButtons: {
    flexDirection: 'row',
    gap: 10
  },
  smallButton: {
    flex: 1,
    paddingVertical: 12
  },
  greenButton: {
    backgroundColor: '#047857'
  },
  yellowButton: {
    backgroundColor: '#F59E0B'
  },
  orangeButton: {
    backgroundColor: '#F97316'
  },
  footer: {
    width: '100%',
    paddingHorizontal: 0,
    paddingVertical: 6,
    alignItems: 'center',
    marginTop: 'auto',
    backgroundColor: colors.background
  },
  footerVersion: {
    fontSize: 12,
    color: colors.muted,
    marginBottom: 20,
    fontWeight: '600'
  },
  footerQuote: {
    fontSize: 12,
    color: colors.text,
    fontStyle: 'italic',
    textAlign: 'center',
    marginBottom: 8,
    fontWeight: '500'
  },
  contactSection: {
    width: '100%',
    alignItems: 'center',
    marginTop: 16
  },
  contactTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.accent
  },
  contactCompany: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.primary
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    paddingHorizontal: 8
  },
  contactIcon: {
    fontSize: 14,
    marginRight: 8,
    marginTop: 1,
    width: 20
  },
  contactLocation: {
    fontSize: 12,
    color: colors.accent,
    fontWeight: '600',
    flex: 1
  },
  contactPhone: {
    fontSize: 13,
    color: colors.primary,
    fontWeight: '700',
    flex: 1
  },
  emailContainer: {
    flex: 1
  },
  contactEmail: {
    fontSize: 11,
    color: colors.email,
    fontWeight: '500',
    marginBottom: 2
  },
  scheduleContainer: {
    flex: 1
  },
  contactSchedule: {
    fontSize: 11,
    color: colors.time,
    fontWeight: '600',
    marginBottom: 1
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 24
  },
  modalContainer: {
    width: '100%',
    maxWidth: 800,  
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    gap: 12,
    ...(Platform.OS === 'web' && {
      width: 'auto',
      minWidth: 480,
      alignSelf: 'center'
    })
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text,
    textAlign: 'center'
  },
  modalSubtitle: {
    fontSize: 12,
    color: colors.muted,
    textAlign: 'center'
  },
  modalField: {
    gap: 6
  },
  modalInlineFields: {
    flexDirection: isSmallScreen ? 'column' : 'row',
    flexWrap: 'wrap',
    gap: 12,
    width: '100%'
  },
  modalFieldHalf: {
    flex: 1,
    minWidth: isSmallScreen ? '100%' : '48%'
  },
  modalInputMasked: {
    fontSize: 12
  },
  modalLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.text
  },
  modalInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 13,
    color: colors.text,
    backgroundColor: '#FFFFFF'
  },
  modalError: {
    fontSize: 12,
    color: '#EF4444',
    textAlign: 'center'
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    gap: 12
  },
  modalButton: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center'
  },
  modalButtonPrimary: {
    backgroundColor: colors.primary
  },
  modalButtonSecondary: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border
  },
  modalButtonDisabled: {
    opacity: 0.5
  },
  modalButtonTextPrimary: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center'
  },
  modalButtonTextSecondary: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '600'
  },
  modalInfo: {
    fontSize: 12,
    color: colors.muted,
    textAlign: 'center',
    marginTop: 8
  },
  consumptionResults: {
    marginTop: 12,
    gap: 8
  },
  consumptionResultItem: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: 10,
    backgroundColor: '#FFFFFF'
  },
  consumptionResultTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 4
  },
  consumptionResultText: {
    fontSize: 12,
    color: colors.muted
  },
  modalSuccess: {
    fontSize: 12,
    color: colors.primary,
    textAlign: 'center',
    marginTop: 8,
    fontWeight: '600'
  },
  reserveContainer: {
    maxHeight: reserveModalMaxHeight,
    width: width > 600 ? 520 : '100%',
    alignSelf: 'stretch'
  },
  reserveScroll: {
    flexGrow: 0,
    maxHeight: reserveModalMaxHeight - 140
  },
  reserveContent: {
    gap: 12,
    paddingVertical: 12,
    paddingBottom: 4
  },
  availableTablesSection: {
    marginTop: 12,
    gap: 8,
    maxHeight: 140,
    overflow: 'hidden'
  },
  availableTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.text
  },
  availableTableItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: colors.border
  },
  availableTableName: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.text
  },
  availableTableInfo: {
    fontSize: 12,
    color: colors.muted
  },
  tableOption: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: 14,
    backgroundColor: '#FFFFFF'
  },
  tableOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: 'rgba(246, 134, 71, 0.12)'
  },
  tableOptionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 4
  },
  tableOptionInfo: {
    fontSize: 12,
    color: colors.muted
  },
  tableAvailableStatus: {
    color: '#22C55E',
    fontWeight: '600',
    marginTop: 4
  },
  reserveActionButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border
  },
  actionButton: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: 10,
    alignItems: 'center',
    borderWidth: 1
  },
  actionButtonPrint: {
    backgroundColor: '#FFFFFF',
    borderColor: colors.primary
  },
  actionButtonWhatsApp: {
    backgroundColor: '#25D366',
    borderColor: '#25D366'
  },
  actionButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text
  },
  kioskContainer: {
    flex: 1,
    backgroundColor: colors.background,
    paddingTop: 20
  },
  kioskHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  kioskTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.text
  },
  kioskCloseButton: {
    fontSize: 28,
    color: colors.text,
    fontWeight: '700'
  },
  kioskContent: {
    padding: 20,
    gap: 12
  },
  kioskSectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 8
  },
  kioskFunctionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    padding: 16,
    borderWidth: 2,
    borderColor: colors.border,
    gap: 12
  },
  kioskFunctionItemHalf: {
    flex: 1
  },
  kioskFunctionActive: {
    borderColor: colors.primary,
    backgroundColor: 'rgba(246, 134, 71, 0.08)'
  },
  kioskCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center'
  },
  kioskCheckmark: {
    fontSize: 16,
    color: colors.primary,
    fontWeight: '700'
  },
  kioskFunctionText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    flex: 1
  },
  kioskExitButton: {
    backgroundColor: '#EF4444',
    marginHorizontal: 20,
    marginBottom: 20,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center'
  },
  kioskExitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700'
  },
  kioskPasswordContainer: {
    maxHeight: '85%',
    paddingBottom: 16
  },
  kioskStatusContainer: {
    alignItems: 'center',
    marginVertical: 12
  },
  kioskStatusBadge: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    alignItems: 'center'
  },
  kioskStatusActive: {
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    borderWidth: 1,
    borderColor: '#22C55E'
  },
  kioskStatusInactive: {
    backgroundColor: 'rgba(107, 114, 128, 0.15)',
    borderWidth: 1,
    borderColor: '#6B7280'
  },
  kioskStatusText: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.text
  },
  kioskFunctionsContainer: {
    marginVertical: 8,
    gap: 12
  },
  kioskFunctionsRow: {
    flexDirection: 'row',
    gap: 10,
    width: '100%'
  },
  kioskFunctionsContent: {
    gap: 12,
    paddingBottom: 8
  },
  chipsPackage: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: colors.border,
    alignItems: 'center'
  },
  chipsPackagePopular: {
    borderColor: colors.primary,
    backgroundColor: 'rgba(246, 134, 71, 0.05)'
  },
  chipsPackageSelected: {
    borderColor: colors.primary,
    backgroundColor: 'rgba(246, 134, 71, 0.15)'
  },
  popularBadge: {
    backgroundColor: colors.primary,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    marginBottom: 8
  },
  popularText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700'
  },
  chipsRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10
  },
  chipsPackageHalf: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8
  },
  chipsPackageText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.text,
    textAlign: 'center',
    marginBottom: 4
  },
  chipsPackagePrice: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.primary,
    textAlign: 'center'
  },
  pixQrContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginVertical: 16,
    gap: 16,
  },
  pixQrColumn: {
    flex: 1,
    minWidth: 300,
    maxWidth: '100%',
    gap: 16,
  },
  pixQrContent: {
    width: '100%',
    alignItems: 'center',
  },
  pixQrBox: {
    width: 180,
    height: 180,
    backgroundColor: colors.background,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.border
  },
  pixQrIcon: {
    fontSize: 48,
    marginBottom: 8
  },
  pixQrText: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.text
  },
  pixQrSubtext: {
    fontSize: 11,
    color: colors.muted,
    marginTop: 4
  },
  pixMockWarning: {
    fontSize: 10,
    color: colors.primary,
    marginTop: 8,
    fontWeight: '600'
  },
  pixCodeContainer: {
    marginVertical: 12,
    gap: 8
  },
  pixCodeLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.text
  },
  pixCodeBox: {
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: colors.border
  },
  pixCodeText: {
    fontSize: 10,
    color: colors.text,
    fontFamily: 'monospace'
  },
  pixCopyButton: {
    backgroundColor: colors.accent,
    borderRadius: 8,
    padding: 12,
    alignItems: 'center'
  },
  pixCopyButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700'
  },
  pixInfoContainer: {
    backgroundColor: colors.background,
    borderRadius: 12,
    padding: 12,
    marginVertical: 12
  },
  pixLabel: {
    fontSize: 12,
    color: colors.muted,
    fontWeight: '600',
    marginTop: 8
  },
  pixValue: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text
  },
  successIcon: {
    fontSize: 48,
    color: '#22C55E',
    textAlign: 'center',
    marginBottom: 8
  },
  ticketContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: colors.border,
    padding: 12,
    marginVertical: 12
  },
  ticketHeader: {
    alignItems: 'center',
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  ticketTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.text
  },
  ticketSubtitle: {
    fontSize: 12,
    color: colors.muted,
    marginTop: 4
  },
  ticketContent: {
    paddingVertical: 12
  },
  ticketRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6
  },
  ticketLabel: {
    fontSize: 11,
    color: colors.muted,
    fontWeight: '600'
  },
  ticketValue: {
    fontSize: 11,
    color: colors.text,
    fontWeight: '700'
  },
  ticketQrContainer: {
    alignItems: 'center',
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border
  },
  ticketQrLabel: {
    fontSize: 11,
    color: colors.muted,
    marginBottom: 8
  },
  ticketQrBox: {
    width: 120,
    height: 120,
    backgroundColor: colors.background,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center'
  },
  ticketQrText: {
    fontSize: 32,
    color: colors.muted
  },
  ticketQrUrl: {
    fontSize: 9,
    color: colors.muted,
    marginTop: 4,
    fontFamily: 'monospace'
  },
  ticketFooter: {
    alignItems: 'center',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.border
  },
  ticketFooterText: {
    fontSize: 10,
    color: colors.muted,
    fontStyle: 'italic'
  },
  reservationSuccessContainer: {
    maxHeight: '90vh',
    width: width > 600 ? 600 : '90%',
    maxWidth: 700
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#22C55E',
    textAlign: 'center',
    marginBottom: 8
  },
  successSubtitle: {
    fontSize: 14,
    color: colors.muted,
    textAlign: 'center',
    marginBottom: 20
  },
  reservationDetails: {
    maxHeight: 400,
    marginBottom: 20
  },
  reservationDetailsContent: {
    gap: 16
  },
  reservationSection: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.border
  },
  reservationSectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 12
  },
  reservationInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB'
  },
  reservationInfoLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.muted,
    flex: 1
  },
  reservationInfoValue: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    flex: 2,
    textAlign: 'right'
  },
  reservationActionButtons: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 16
  },
  reservationActionButton: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1
  },
  reservationActionButtonWhatsApp: {
    backgroundColor: '#25D366',
    borderColor: '#25D366'
  },
  reservationActionButtonPrint: {
    backgroundColor: '#FFFFFF',
    borderColor: colors.primary
  },
  reservationActionButtonPDF: {
    backgroundColor: '#EF4444',
    borderColor: '#EF4444'
  },
  reservationActionButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#FFFFFF'
  },
  reservationActionButtonPrintText: {
    color: colors.primary
  }
});
